"use client";

import { useState, useCallback } from "react";
import { AlertCircle } from "lucide-react";
import { payrollApi, type CalculationResponse } from "@/lib/api";
import { formatInputValue, parseInputValue, formatMoney as fmt } from "@/lib/format";

type CalculatorMode = "netpay" | "grosspay";

export function NetpayCalculator() {
  const [activeTab, setActiveTab] = useState<CalculatorMode>("netpay");
  const [grossSalary, setGrossSalary] = useState<string>("");
  const [targetNetSalary, setTargetNetSalary] = useState<string>("");
  const [nonCashBenefits, setNonCashBenefits] = useState<string>("");
  const [pensionContribution, setPensionContribution] = useState<string>("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculation, setCalculation] = useState<CalculationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCalculate = useCallback(async () => {
    setError(null);
    setIsCalculating(true);

    try {
      const result = await payrollApi.calculateSalary({
        calculationType: activeTab === "netpay" ? "GROSS_TO_NET" : "NET_TO_GROSS",
        grossSalary: activeTab === "netpay" ? parseInputValue(grossSalary) : undefined,
        targetNetSalary: activeTab === "grosspay" ? parseInputValue(targetNetSalary) : undefined,
        nonCashBenefits: parseInputValue(nonCashBenefits),
        pensionContribution: parseInputValue(pensionContribution),
      });

      setCalculation(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to calculate salary");
      setCalculation(null);
    } finally {
      setIsCalculating(false);
    }
  }, [activeTab, grossSalary, targetNetSalary, nonCashBenefits, pensionContribution]);

  const handleTabChange = useCallback((mode: CalculatorMode) => {
    setActiveTab(mode);
    setCalculation(null);
    setError(null);
    if (mode === "grosspay") {
      setNonCashBenefits("");
      setPensionContribution("");
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleCalculate();
    },
    [handleCalculate]
  );

  return (
    <div className="mx-auto max-w-5xl">
      {/* Mode selector — editorial tab bar */}
      <div className="grid grid-cols-2 border-b border-[#DCE3EA]">
        <button
          onClick={() => handleTabChange("netpay")}
          className={`py-3.5 text-[13px] font-medium transition ${
            activeTab === "netpay"
              ? "border-b-2 border-slate-950 text-slate-950"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          Gross → Net
        </button>
        <button
          onClick={() => handleTabChange("grosspay")}
          className={`py-3.5 text-[13px] font-medium transition ${
            activeTab === "grosspay"
              ? "border-b-2 border-slate-950 text-slate-950"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          Net → Gross
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr]">
        {/* Input section */}
        <div className="border-b border-[#DCE3EA] lg:border-b-0 lg:border-r">
          <div className="px-0 py-8 lg:pr-10">
            {activeTab === "netpay" ? (
              <div className="space-y-5">
                <div>
                  <label htmlFor="grossSalary" className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                    Gross salary (KES)
                  </label>
                  <div className="relative">
                    <input
                      id="grossSalary"
                      type="text"
                      inputMode="decimal"
                      placeholder="120,000"
                      value={grossSalary}
                      onChange={(e) => setGrossSalary(formatInputValue(e.target.value))}
                      onKeyDown={handleKeyDown}
                      className="w-full border-b-2 border-[#DCE3EA] bg-transparent pb-2 font-[family-name:var(--font-source-serif)] text-[28px] font-medium text-slate-950 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nonCashBenefits" className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                      Non-cash benefits
                    </label>
                    <input
                      id="nonCashBenefits"
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={nonCashBenefits}
                      onChange={(e) => setNonCashBenefits(formatInputValue(e.target.value))}
                      onKeyDown={handleKeyDown}
                      className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                  <div>
                    <label htmlFor="pensionContribution" className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                      Pension contribution
                    </label>
                    <input
                      id="pensionContribution"
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={pensionContribution}
                      onChange={(e) => setPensionContribution(formatInputValue(e.target.value))}
                      onKeyDown={handleKeyDown}
                      className="w-full border-b border-[#DCE3EA] bg-transparent pb-2 text-[15px] font-medium text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="targetNetSalary" className="mono mb-2 block text-[9px] uppercase tracking-[.13em] text-slate-400">
                  Target net salary (KES)
                </label>
                <input
                  id="targetNetSalary"
                  type="text"
                  inputMode="decimal"
                  placeholder="85,000"
                  value={targetNetSalary}
                  onChange={(e) => setTargetNetSalary(formatInputValue(e.target.value))}
                  onKeyDown={handleKeyDown}
                  className="w-full border-b-2 border-[#DCE3EA] bg-transparent pb-2 font-[family-name:var(--font-source-serif)] text-[28px] font-medium text-slate-950 outline-none transition-colors placeholder:text-slate-300 focus:border-[#4B91F7]"
                />
              </div>
            )}

            <button
              onClick={handleCalculate}
              disabled={
                (activeTab === "netpay" && (!grossSalary || parseInputValue(grossSalary) <= 0)) ||
                (activeTab === "grosspay" && (!targetNetSalary || parseInputValue(targetNetSalary) <= 0)) ||
                isCalculating
              }
              className="ink-button mt-8 w-full border border-slate-950 bg-slate-950 px-5 py-3 text-[13px] font-medium text-white transition hover:border-[#2348C7] hover:bg-[#2348C7] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isCalculating ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Calculating…
                </span>
              ) : (
                "Calculate"
              )}
            </button>

            <div className="mt-4 grid grid-cols-4 gap-2">
              {(activeTab === "netpay"
                ? [50000, 100000, 150000, 200000]
                : [40000, 80000, 120000, 160000]
              ).map((amount) => (
                <button
                  key={amount}
                  onClick={() =>
                    activeTab === "netpay"
                      ? setGrossSalary(formatInputValue(amount.toString()))
                      : setTargetNetSalary(formatInputValue(amount.toString()))
                  }
                  className="border border-[#DCE3EA] bg-white px-2 py-2 text-[11px] font-medium text-slate-600 transition hover:bg-[#F3F6FA] hover:text-slate-950"
                >
                  {amount / 1000}K
                </button>
              ))}
            </div>

            {error && (
              <div className="mt-5 flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Results section — ledger style */}
        <div className="py-8 lg:pl-10">
          {calculation ? (
            <div>
              {/* Summary row */}
              <div className="grid grid-cols-3 border-b border-[#DCE3EA]">
                <div className="border-r border-[#DCE3EA] pb-5 pr-4">
                  <span className="mono block text-[9px] uppercase tracking-[.13em] text-slate-400">Gross</span>
                  <strong className="mt-1.5 block text-lg font-medium">{fmt(calculation.grossSalary ?? 0)}</strong>
                </div>
                <div className="border-r border-[#DCE3EA] px-4 pb-5">
                  <span className="mono block text-[9px] uppercase tracking-[.13em] text-slate-400">Deductions</span>
                  <strong className="mt-1.5 block text-lg font-medium text-red-500">
                    −{fmt(
                      (calculation.deductions?.nssf ?? 0) +
                      (calculation.deductions?.nhif ?? 0) +
                      (calculation.deductions?.housingLevy ?? 0) +
                      (calculation.deductions?.paye ?? 0)
                    )}
                  </strong>
                </div>
                <div className="pl-4 pb-5">
                  <span className="mono block text-[9px] uppercase tracking-[.13em] text-slate-400">Net pay</span>
                  <strong className="mt-1.5 block text-lg font-medium text-[#0F7B4D]">{fmt(calculation.netSalary ?? 0)}</strong>
                </div>
              </div>

              {/* Deductions ledger */}
              <div className="mt-6">
                <div className="mono mb-2 grid grid-cols-[1fr_100px] gap-3 border-b border-[#DCE3EA] pb-2 text-[9px] uppercase tracking-[.08em] text-slate-400">
                  <span>Deduction</span>
                  <span className="text-right">Amount</span>
                </div>
                <div className="text-[13px] text-slate-700">
                  <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-3">
                    <span>PAYE</span>
                    <span className="mono text-right">{fmt(calculation.deductions?.paye ?? 0)}</span>
                  </div>
                  <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-3">
                    <span>NSSF</span>
                    <span className="mono text-right">{fmt(calculation.deductions?.nssf ?? 0)}</span>
                  </div>
                  <div className="grid grid-cols-[1fr_100px] gap-3 border-b border-[#E8E7E1] py-3">
                    <span>SHIF</span>
                    <span className="mono text-right">{fmt(calculation.deductions?.nhif ?? 0)}</span>
                  </div>
                  <div className="grid grid-cols-[1fr_100px] gap-3 py-3">
                    <span>Housing Levy</span>
                    <span className="mono text-right">{fmt(calculation.deductions?.housingLevy ?? 0)}</span>
                  </div>
                </div>
              </div>

              {/* Tax details */}
              <div className="mt-6 border-t border-[#DCE3EA] pt-5">
                <div className="mono mb-3 text-[9px] uppercase tracking-[.13em] text-slate-400">Tax details</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
                  <div className="flex justify-between border-b border-[#E8E7E1] pb-2">
                    <span className="text-slate-500">Taxable income</span>
                    <span className="mono font-medium">{fmt(calculation.taxDetails.taxableIncome)}</span>
                  </div>
                  <div className="flex justify-between border-b border-[#E8E7E1] pb-2">
                    <span className="text-slate-500">Non-cash benefits</span>
                    <span className="mono font-medium">{fmt(calculation.nonCashBenefits ?? 0)}</span>
                  </div>
                  <div className="flex justify-between border-b border-[#E8E7E1] pb-2">
                    <span className="text-slate-500">Personal relief</span>
                    <span className="mono font-medium text-[#0F7B4D]">−{fmt(calculation.taxDetails.personalRelief)}</span>
                  </div>
                  <div className="flex justify-between border-b border-[#E8E7E1] pb-2">
                    <span className="text-slate-500">Pension</span>
                    <span className="mono font-medium">{fmt(calculation.pensionContribution ?? 0)}</span>
                  </div>
                </div>
              </div>

              {/* Net take-home footer */}
              <div className="mt-6 border-t-2 border-slate-950 pt-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-[13px] font-medium text-slate-950">Net Take-Home</span>
                  <span className="font-[family-name:var(--font-source-serif)] text-[28px] font-medium text-slate-950">
                    {fmt(calculation.netSalary ?? 0)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
              <p className="mono text-[10px] uppercase tracking-[.13em] text-slate-400">
                {activeTab === "netpay" ? "Enter a gross salary" : "Enter a target net salary"}
              </p>
              <p className="mt-2 text-[13px] text-slate-500">
                Your detailed payroll breakdown will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}